import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { CommonModule, NgForOf, NgIf } from '@angular/common';
import { Content } from '@shared/models/content.model';
import { ContentService } from '@services/content/content.service';
import { CoupleIdLite } from '@shared/models/generic/id/couple-id-lite.model';
import { firstValueFrom } from 'rxjs';
import { animate, style, transition, trigger } from '@angular/animations';

@Component({
  selector: 'app-release-note',
  templateUrl: './release-note.component.html',
  styleUrls: ['./release-note.component.scss'],
  imports: [NgIf, NgForOf, CommonModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [
    trigger('carouselAnimation', [
      transition(':increment', [
        style({ opacity: 0, transform: 'translateX(20px)' }),
        animate('600ms ease-out', style({ opacity: 1, transform: 'translateX(0)' })),
      ]),
      transition(':decrement', [
        style({ opacity: 0, transform: 'translateX(-20px)' }),
        animate('600ms ease-out', style({ opacity: 1, transform: 'translateX(0)' })),
      ]),
    ]),
  ],
})
export class ReleaseNoteComponent implements OnInit {
  @Input() releases: CoupleIdLite[] = [];
  @Output() closeRelease = new EventEmitter<void>();

  currentIndex = 0;
  release: Content;

  private readonly releaseCache = new Map<string, Content>();

  constructor(
    private readonly contentService: ContentService,
    private readonly cdr: ChangeDetectorRef,
  ) {}

  get canGoPrevious(): boolean {
    return this.releases?.length ? this.currentIndex > 0 : false;
  }

  get canGoNext(): boolean {
    return this.releases?.length ? this.currentIndex < this.releases.length - 1 : false;
  }

  ngOnInit(): void {
    if (this.releases?.length) {
      this.initReleases();
    }
  }

  onClose(): void {
    this.closeRelease.emit();
  }

  async next(): Promise<void> {
    if (!this.canGoNext) {
      return;
    }
    const nextIndex = this.currentIndex + 1;
    await this.load(nextIndex);
    this.currentIndex = nextIndex;
  }

  async previous(): Promise<void> {
    if (!this.canGoPrevious) {
      return;
    }
    const prevIndex = this.currentIndex - 1;
    await this.load(prevIndex);
    this.currentIndex = prevIndex;
  }

  async goTo(index: number): Promise<void> {
    if (!this.releases?.length) {
      return;
    }
    if (index < 0 || index > this.releases.length - 1) {
      return;
    }
    await this.load(index);
  }

  async load(index: number): Promise<void> {
    if (!this.releases?.length) {
      return;
    }
    this.currentIndex = index;

    const key = JSON.stringify(this.releases[index]);
    const cached = this.releaseCache.get(key);
    if (cached) {
      this.release = cached;
      this.cdr.markForCheck();
      return;
    }

    const content = await firstValueFrom(this.contentService.getContent(this.releases[index]));
    if (content) {
      this.releaseCache.set(key, content);
      this.release = content;
      this.cdr.markForCheck();
    }
  }

  resizeCoverImage(release: Content): string {
    const cover = release.resourceMedias.find((m) => m.isCover);
    const image = cover ? cover.media.original : release.resourceMedias[0].media.original;
    return image.replace('/original/', '/') + '?h=800&w=600&fit=scale';
  }

  hasImage(release: Content): boolean {
    return release.resourceMedias?.length > 0;
  }

  private async initReleases(): Promise<void> {
    await this.load(0);
  }
}
